import React from 'react';
import { Plus, Lock } from 'lucide-react';
import SimpleCollegeProgress from './SimpleCollegeProgress';

/**
 * SchoolCategorySection - Reusable component for displaying a category of schools
 * with drag-and-drop support, progress tracking, and placeholder cards
 *
 * Used in both CollegeJourneyHub and EnhancedCollegeDataHub for consistent UI
 */
const SchoolCategorySection = ({
  title,
  colleges,
  categoryKey,
  maxCount = 4,
  onAddSchool,
  dragOverCategory,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  renderCollegeCard,
  lockedCategories,
  onToggleLock,
  customTargets
}) => {
  const categoryConfig = {
    reach: {
      label: 'Reach Schools',
      color: 'coral',
      minCount: 2,
      recommendedMin: customTargets?.reach || 4,
      recommendedMax: customTargets?.reach || 4
    },
    target: {
      label: 'Target Schools',
      color: 'gold',
      minCount: 2,
      recommendedMin: customTargets?.target || 4,
      recommendedMax: customTargets?.target || 4
    },
    safety: {
      label: 'Safety Schools',
      color: 'teal',
      minCount: 2,
      recommendedMin: customTargets?.safety || 4,
      recommendedMax: customTargets?.safety || 4
    },
    other: {
      label: 'Other Schools',
      color: 'gray',
      minCount: 0,
      recommendedMin: 0,
      recommendedMax: 10
    },
    additional: {
      label: 'Other Schools',
      color: 'gray',
      minCount: 0,
      recommendedMin: 0,
      recommendedMax: 10
    }
  };

  const config = categoryConfig[categoryKey] || {
    label: title || 'Schools',
    color: 'gray',
    minCount: 1,
    recommendedMin: maxCount,
    recommendedMax: maxCount
  };

  const currentCount = colleges.length;
  const placeholderCount = Math.max(0, maxCount - currentCount);
  const isLocked = lockedCategories?.[categoryKey] || false;
  const isDraggingOverThis = dragOverCategory === categoryKey;

  return (
    <div
      className={`mb-8 relative transition-all ${
        isDraggingOverThis
          ? isLocked
            ? 'bg-red-50 border-2 border-dashed border-red-400 rounded-lg p-4'
            : 'bg-azure-50 border-2 border-dashed border-azure-400 rounded-lg p-4'
          : ''
      }`}
      onDragOver={(e) => handleDragOver && handleDragOver(e, categoryKey)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop && handleDrop(e, categoryKey)}
    >
      {/* Locked Category Warning - Shows when dragging over locked section */}
      {isDraggingOverThis && isLocked && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-50/95 rounded-lg border-2 border-red-400 border-dashed">
          <div className="flex items-center gap-3 bg-white px-6 py-4 rounded-lg shadow-lg border-2 border-red-500">
            <Lock className="w-5 sm:w-6 h-5 sm:h-6 text-red-600" />
            <div>
              <p className="font-bold text-red-900">This category is locked</p>
              <p className="text-sm text-red-700">Unlock to add or move schools here</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">{config.label}</h3>

        {/* Progress Display with Lock */}
        <SimpleCollegeProgress
          categoryKey={categoryKey}
          currentCount={currentCount}
          targetCount={config.recommendedMin || maxCount}
          isLocked={isLocked}
          onToggleLock={onToggleLock}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {colleges.map(college => renderCollegeCard(college, categoryKey))}

        {/* Placeholder cards */}
        {[...Array(placeholderCount)].map((_, index) => (
          <button
            key={`placeholder-${categoryKey}-${index}`}
            onClick={onAddSchool}
            className="bg-stone-50 border border-dashed border-stone-300 rounded-lg p-5 hover:border-stone-400 hover:bg-stone-100 transition-all text-center group min-h-[140px]"
          >
            <div className="flex flex-col items-center justify-center h-full">
              <Plus className="w-5 sm:w-6 h-5 sm:h-6 text-stone-400 group-hover:text-stone-600 mb-2" />
              <div className="text-sm font-medium text-stone-500 group-hover:text-stone-700">
                Add {categoryKey === 'reach' || categoryKey === 'reach' ? 'Reach' :
                      categoryKey === 'target' ? 'Target' :
                      categoryKey === 'safety' ? 'Safety' :
                      categoryKey === 'other' || categoryKey === 'additional' ? 'Other' : categoryKey} School
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SchoolCategorySection;